// src/shared/lib/qrUtils.js
// Lightweight QR code display component using the qrcode npm package.
// Usage: <QRCodeDisplay value="JT-GLD-001" size={128} label="JT-GLD-001" />
// Install: npm install qrcode

import { useEffect, useRef } from 'react'

// Sequence number generator — resets on new financial year (Apr 1)
// In production this would come from the backend.
export function getFinYear() {
  const now  = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1 // 1-based
  return month >= 4 ? year : year - 1
}

export function formatRollSeq(seq, finYear) {
  // e.g. FY2425-00042
  const fy = String(finYear).slice(2) + String(finYear + 1).slice(2)
  return `FY${fy}-${String(seq).padStart(5, '0')}`
}

// QR code display component
export function QRCodeDisplay({ value, size = 128, label, sublabel }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    if (!canvasRef.current || !value) return

    const drawPlaceholder = () => {
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return
      ctx.fillStyle = '#F0F5FA'
      ctx.fillRect(0, 0, size, size)
      ctx.fillStyle = '#1D6FA8'
      ctx.font = '11px monospace'
      ctx.textAlign = 'center'
      ctx.fillText('QR', size / 2, size / 2 - 6)
      ctx.fillText(String(value).slice(0, 10), size / 2, size / 2 + 8)
    }

    // Dynamically import qrcode. CRA/webpack wraps this CommonJS module's
    // exports under `.default` in the dynamic-import namespace, so unwrap it —
    // otherwise `QRCode.toCanvas` is undefined and we'd fall back to the
    // placeholder even though the package is installed.
    import('qrcode')
      .then(mod => {
        const QRCode = mod?.default ?? mod
        return QRCode.toCanvas(canvasRef.current, String(value), {
          width: size,
          margin: 1,
          color: { dark: '#042C53', light: '#FFFFFF' },
        })
      })
      .catch(err => {
        console.error('QRCodeDisplay: failed to render QR for', value, err)
        drawPlaceholder()
      })
  }, [value, size])

  return (
    <div className="text-center">
      <div className="inline-block border border-gray-200 rounded-xl p-2 bg-white">
        <canvas ref={canvasRef} width={size} height={size} style={{ display: 'block', borderRadius: 8 }} />
      </div>
      {label    && <p className="text-xs font-medium text-brand-700 mt-1.5">{label}</p>}
      {sublabel && <p className="text-[11px] text-gray-400 mt-0.5">{sublabel}</p>}
    </div>
  )
}

// Print a QR code — opens a new window with just the canvas
export function printQR(value, label) {
  const win = window.open('', '_blank', 'width=300,height=350')
  win.document.write(`
    <html><head><title>${label}</title>
    <style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif}</style>
    </head><body>
    <canvas id="qr" width="200" height="200"></canvas>
    <p style="margin-top:8px;font-size:13px;font-weight:600">${label}</p>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></scr` + `ipt>
    <script>new QRCode(document.getElementById('qr'),{text:'${value}',width:200,height:200,colorDark:'#042C53',colorLight:'#ffffff'})</scr` + `ipt>
    </body></html>`)
  win.document.close()
  setTimeout(() => win.print(), 800)
}
